/**
 * Mock Lending API Implementation
 * Provides realistic mock data for development and testing
 * Uses faker.js for consistent, realistic data generation
 *
 * FIXED: Added data caching to prevent HMR loops and unnecessary re-renders
 */

import { faker } from '@faker-js/faker';
import { LoanStatus, CollateralType, WorkflowStatus, EntityType } from '@/types/api/lending';
import type {
  Community,
  Resident,
  CollateralAsset,
  BankPartner,
  CommunityLoanProposal,
  LoanProposalWithDetails,
  BankProposalQueue,
  RiskAssessment,
  PortfolioSummary,
  FinancialMetrics,
  ApiResponse,
  PaginatedResponse,
} from '@/types/api/lending';

// ============================================================================
// DATA CACHING - Prevent HMR loops by maintaining stable data references
// ============================================================================

interface DataCache {
  communities: Map<string, Community>;
  residents: Map<string, Resident>;
  collateralAssets: Map<string, CollateralAsset>;
  proposals: Map<string, LoanProposalWithDetails>;
  bankQueues: Map<string, BankProposalQueue>;
  riskAssessments: Map<string, RiskAssessment>;
  portfolioSummaries: Map<string, PortfolioSummary>;
  financialMetrics: Map<string, FinancialMetrics>;
  lastGenerated: number;
}

const cache: DataCache = {
  communities: new Map(),
  residents: new Map(),
  collateralAssets: new Map(),
  proposals: new Map(),
  bankQueues: new Map(),
  riskAssessments: new Map(),
  portfolioSummaries: new Map(),
  financialMetrics: new Map(),
  lastGenerated: 0,
};

// Cache TTL - 5 minutes in development, longer in production
const CACHE_TTL = import.meta.env.DEV ? 5 * 60 * 1000 : 30 * 60 * 1000;

const isCacheExpired = () => Date.now() - cache.lastGenerated > CACHE_TTL;

const clearExpiredCache = () => {
  if (isCacheExpired()) {
    Object.values(cache).forEach((map) => {
      if (map instanceof Map) map.clear();
    });
    cache.lastGenerated = Date.now();
  }
};

// ============================================================================
// MOCK DATA GENERATORS
// ============================================================================

const mockDataGenerators = {
  community: (): Community => ({
    id: faker.string.uuid(),
    name: faker.company.name() + ' Community',
    address: faker.location.streetAddress(),
    city: faker.location.city(),
    state: faker.location.state({ abbreviated: true }),
    zip_code: faker.location.zipCode(),
    country: 'US',
    total_units: faker.number.int({ min: 50, max: 500 }),
    occupied_units: faker.number.int({ min: 30, max: 450 }),
    community_type: faker.helpers.arrayElement([
      'Apartment Complex',
      'Townhomes',
      'Single Family',
      'Mixed Use',
    ]),
    established_date: faker.date.past({ years: 20 }).toISOString().split('T')[0],
    management_company: faker.datatype.boolean() ? faker.company.name() : undefined,
    contact_email: faker.internet.email(),
    contact_phone: faker.phone.number(),
    website: faker.datatype.boolean() ? faker.internet.url() : undefined,
    amenities: faker.helpers.arrayElements(
      ['Pool', 'Gym', 'Clubhouse', 'Parking', 'Laundry', 'Pet Friendly'],
      { min: 2, max: 5 },
    ),
    created_at: faker.date.past().toISOString(),
    updated_at: faker.date.recent().toISOString(),
  }),

  resident: (): Resident => {
    const leaseStart = faker.date.past({ years: 2 });
    const leaseEnd = faker.date.future({ years: 1, refDate: leaseStart });

    return {
      id: faker.string.uuid(),
      community_id: faker.string.uuid(),
      user_id: faker.string.uuid(),
      unit_number: faker.number.int({ min: 100, max: 999 }).toString(),
      lease_start_date: leaseStart.toISOString().split('T')[0],
      lease_end_date: leaseEnd.toISOString().split('T')[0],
      rent_amount: faker.number.float({ min: 800, max: 3000, fractionDigits: 2 }),
      security_deposit: faker.number.float({ min: 500, max: 2000, fractionDigits: 2 }),
      move_in_date: leaseStart.toISOString().split('T')[0],
      move_out_date: faker.datatype.boolean()
        ? faker.date.recent().toISOString().split('T')[0]
        : undefined,
      resident_status: faker.helpers.arrayElement(['active', 'inactive', 'pending'] as const),
      emergency_contact_name: faker.person.fullName(),
      emergency_contact_phone: faker.phone.number(),
      created_at: faker.date.past().toISOString(),
      updated_at: faker.date.recent().toISOString(),
    };
  },

  collateralAsset: (): CollateralAsset => {
    const assetType = faker.helpers.arrayElement(Object.values(CollateralType));
    const currentValue = faker.number.float({ min: 5000, max: 200000, fractionDigits: 2 });

    return {
      id: faker.string.uuid(),
      community_id: faker.string.uuid(),
      asset_type: assetType,
      asset_description: `${assetType.replace('_', ' ')} - ${faker.commerce.productName()}`,
      estimated_value: currentValue,
      appraisal_date: faker.datatype.boolean()
        ? faker.date.recent({ days: 90 }).toISOString().split('T')[0]
        : undefined,
      appraisal_value: faker.datatype.boolean()
        ? faker.number.float({
            min: currentValue * 0.8,
            max: currentValue * 1.2,
            fractionDigits: 2,
          })
        : undefined,
      condition_rating: faker.number.int({ min: 6, max: 10 }),
      location: faker.location.streetAddress(),
      ownership_documentation: faker.datatype.boolean() ? 'deed-on-file' : undefined,
      created_at: faker.date.past().toISOString(),
      updated_at: faker.date.recent().toISOString(),
    };
  },

  bankPartner: (): BankPartner => ({
    id: faker.string.uuid(),
    bank_name: faker.company.name() + ' Bank',
    bank_code: faker.finance.routingNumber(),
    contact_person: faker.person.fullName(),
    contact_email: faker.internet.email(),
    contact_phone: faker.phone.number(),
    address: faker.location.streetAddress(),
    city: faker.location.city(),
    state: faker.location.state({ abbreviated: true }),
    zip_code: faker.location.zipCode(),
    country: 'US',
    website: faker.internet.url(),
    partnership_status: faker.helpers.arrayElement(['active', 'inactive', 'pending'] as const),
    partnership_start_date: faker.date.past({ years: 3 }).toISOString().split('T')[0],
    partnership_end_date: faker.datatype.boolean()
      ? faker.date.future({ years: 2 }).toISOString().split('T')[0]
      : undefined,
    created_at: faker.date.past().toISOString(),
    updated_at: faker.date.recent().toISOString(),
  }),

  loanProposal: (): CommunityLoanProposal => {
    const submittedDate = faker.date.recent({ days: 30 });
    const reviewDeadline = new Date(submittedDate.getTime() + 14 * 24 * 60 * 60 * 1000);

    return {
      id: faker.string.uuid(),
      community_id: faker.string.uuid(),
      requesting_resident_id: faker.datatype.boolean() ? faker.string.uuid() : undefined,
      loan_amount: faker.number.float({ min: 5000, max: 100000, fractionDigits: 2 }),
      loan_purpose: faker.helpers.arrayElement([
        'Emergency Repairs',
        'Capital Improvements',
        'Equipment Purchase',
        'Cash Flow Bridge',
        'Refinancing',
        'Expansion Project',
      ]),
      requested_term_months: faker.helpers.arrayElement([12, 24, 36, 48, 60]),
      proposed_interest_rate: faker.datatype.boolean()
        ? faker.number.float({ min: 3.5, max: 8.5, fractionDigits: 2 })
        : undefined,
      collateral_asset_ids: Array.from({ length: faker.number.int({ min: 1, max: 3 }) }, () =>
        faker.string.uuid(),
      ),
      loan_status: faker.helpers.arrayElement(Object.values(LoanStatus)),
      submitted_date: submittedDate.toISOString().split('T')[0],
      review_deadline: reviewDeadline.toISOString().split('T')[0],
      approved_date: faker.datatype.boolean()
        ? faker.date.between({ from: submittedDate, to: new Date() }).toISOString().split('T')[0]
        : undefined,
      funded_date: faker.datatype.boolean()
        ? faker.date.recent().toISOString().split('T')[0]
        : undefined,
      rejection_reason: faker.datatype.boolean() ? faker.lorem.sentence() : undefined,
      created_at: faker.date.past().toISOString(),
      updated_at: faker.date.recent().toISOString(),
    };
  },

  riskAssessment: (): RiskAssessment => ({
    proposal_id: faker.string.uuid(),
    credit_score: faker.number.int({ min: 580, max: 850 }),
    debt_to_income_ratio: faker.number.float({ min: 0.1, max: 0.6, fractionDigits: 3 }),
    loan_to_value_ratio: faker.number.float({ min: 0.6, max: 0.9, fractionDigits: 3 }),
    collateral_coverage_ratio: faker.number.float({ min: 1.0, max: 2.5, fractionDigits: 2 }),
    community_financial_health: faker.number.int({ min: 5, max: 10 }),
    risk_rating: faker.helpers.arrayElement(['low', 'medium', 'high', 'very_high'] as const),
    risk_factors: faker.helpers.arrayElements(
      [
        'High debt-to-income ratio',
        'Limited collateral coverage',
        'Poor payment history',
        'Market volatility',
        'Regulatory concerns',
        'Concentration risk',
      ],
      { min: 0, max: 3 },
    ),
    recommendations: faker.helpers.arrayElements(
      [
        'Require additional collateral',
        'Increase interest rate',
        'Reduce loan amount',
        'Implement stricter covenants',
        'Quarterly reviews',
        'Escrow requirements',
      ],
      { min: 1, max: 3 },
    ),
    assessed_by: faker.person.fullName(),
    assessed_at: faker.date.recent().toISOString(),
  }),
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const simulateDelay = (min = 100, max = 500): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, faker.number.int({ min, max })));

const createApiResponse = <T>(data: T, message?: string): ApiResponse<T> => ({
  data,
  success: true,
  message,
  timestamp: new Date().toISOString(),
});

const createPaginatedResponse = <T>(
  data: T[],
  page = 1,
  limit = 10,
  total?: number,
): PaginatedResponse<T> => ({
  data: data.slice((page - 1) * limit, page * limit),
  pagination: {
    page,
    limit,
    total: total ?? data.length,
    total_pages: Math.ceil((total ?? data.length) / limit),
  },
  success: true,
  timestamp: new Date().toISOString(),
});

// Simple hash function for consistent seeding
const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
};

// ============================================================================
// MOCK API ENDPOINTS
// ============================================================================

export const mockLendingAPI = {
  // Communities
  communities: {
    async getAll(): Promise<ApiResponse<Community[]>> {
      await simulateDelay();
      const communities = Array.from(
        { length: faker.number.int({ min: 5, max: 15 }) },
        mockDataGenerators.community,
      );
      return createApiResponse(communities);
    },

    async getById(id: string): Promise<ApiResponse<Community>> {
      await simulateDelay();
      const community = mockDataGenerators.community();
      community.id = id;
      return createApiResponse(community);
    },

    async getFinancials(communityId: string): Promise<ApiResponse<Record<string, number>>> {
      await simulateDelay();
      return createApiResponse({
        treasuryBalance: faker.number.float({ min: 100000, max: 1000000, fractionDigits: 2 }),
        availableForLoans: faker.number.float({ min: 50000, max: 500000, fractionDigits: 2 }),
        monthlyRevenue: faker.number.float({ min: 20000, max: 150000, fractionDigits: 2 }),
        occupancyRate: faker.number.float({ min: 0.85, max: 0.98, fractionDigits: 3 }),
      });
    },
  },

  // Residents
  residents: {
    async getByCommunity(communityId: string): Promise<ApiResponse<Resident[]>> {
      await simulateDelay();
      const residents = Array.from({ length: faker.number.int({ min: 10, max: 50 }) }, () => {
        const resident = mockDataGenerators.resident();
        resident.community_id = communityId;
        return resident;
      });
      return createApiResponse(residents);
    },

    async getById(id: string): Promise<ApiResponse<Resident>> {
      await simulateDelay();
      const resident = mockDataGenerators.resident();
      resident.id = id;
      return createApiResponse(resident);
    },
  },

  // Collateral
  collateral: {
    async getByCommunity(communityId: string): Promise<ApiResponse<CollateralAsset[]>> {
      await simulateDelay();
      const assets = Array.from({ length: faker.number.int({ min: 5, max: 20 }) }, () => {
        const asset = mockDataGenerators.collateralAsset();
        asset.community_id = communityId;
        return asset;
      });
      return createApiResponse(assets);
    },

    async getById(id: string): Promise<ApiResponse<CollateralAsset>> {
      await simulateDelay();
      const asset = mockDataGenerators.collateralAsset();
      asset.id = id;
      return createApiResponse(asset);
    },
  },

  // Bank Operations
  bank: {
    async getProposalQueue(bankId: string): Promise<ApiResponse<BankProposalQueue>> {
      await simulateDelay();

      // Check cache first
      const cacheKey = `queue-${bankId}`;
      if (cache.bankQueues.has(cacheKey) && !isCacheExpired()) {
        return createApiResponse(cache.bankQueues.get(cacheKey)!);
      }

      // Generate stable data
      faker.seed(hashString(bankId)); // Use consistent seed for same bankId

      const proposals = Array.from(
        { length: faker.number.int({ min: 8, max: 25 }) },
        (_, index) => {
          // Use consistent seed for each proposal
          faker.seed(hashString(`${bankId}-proposal-${index}`));

          const proposal = mockDataGenerators.loanProposal();
          const community = mockDataGenerators.community();
          const collateralAssets = proposal.collateral_asset_ids.map((id, assetIndex) => {
            faker.seed(hashString(`${bankId}-asset-${index}-${assetIndex}`));
            const asset = mockDataGenerators.collateralAsset();
            asset.id = id;
            asset.community_id = community.id;
            return asset;
          });

          return {
            ...proposal,
            community,
            collateral_assets: collateralAssets,
            workflows: [],
          } as LoanProposalWithDetails;
        },
      );

      const queue: BankProposalQueue = {
        proposals,
        total_count: proposals.length,
        pending_count: proposals.filter((p) => p.loan_status === 'pending').length,
        under_review_count: proposals.filter((p) => p.loan_status === 'under_review').length,
        overdue_count: proposals.filter(
          (p) => p.review_deadline && new Date(p.review_deadline) < new Date(),
        ).length,
      };

      // Cache the result
      cache.bankQueues.set(cacheKey, queue);
      cache.lastGenerated = Date.now();

      return createApiResponse(queue);
    },

    async getProposal(proposalId: string): Promise<ApiResponse<LoanProposalWithDetails>> {
      await simulateDelay();

      // Check cache first
      if (cache.proposals.has(proposalId) && !isCacheExpired()) {
        return createApiResponse(cache.proposals.get(proposalId)!);
      }

      // Generate stable data
      faker.seed(hashString(proposalId));

      const proposal = mockDataGenerators.loanProposal();
      proposal.id = proposalId;

      const community = mockDataGenerators.community();
      const collateralAssets = proposal.collateral_asset_ids.map((id, index) => {
        faker.seed(hashString(`${proposalId}-asset-${index}`));
        const asset = mockDataGenerators.collateralAsset();
        asset.id = id;
        asset.community_id = community.id;
        return asset;
      });

      const proposalWithDetails: LoanProposalWithDetails = {
        ...proposal,
        community,
        collateral_assets: collateralAssets,
        workflows: [],
      };

      // Cache the result
      cache.proposals.set(proposalId, proposalWithDetails);
      cache.lastGenerated = Date.now();

      return createApiResponse(proposalWithDetails);
    },

    async getRiskAssessment(proposalId: string): Promise<ApiResponse<RiskAssessment>> {
      await simulateDelay();

      // Check cache first
      if (cache.riskAssessments.has(proposalId) && !isCacheExpired()) {
        return createApiResponse(cache.riskAssessments.get(proposalId)!);
      }

      // Generate stable data
      faker.seed(hashString(`risk-${proposalId}`));

      const assessment = mockDataGenerators.riskAssessment();
      assessment.proposal_id = proposalId;

      // Cache the result
      cache.riskAssessments.set(proposalId, assessment);
      cache.lastGenerated = Date.now();

      return createApiResponse(assessment);
    },

    async getPortfolioSummary(bankId: string): Promise<ApiResponse<PortfolioSummary>> {
      await simulateDelay();

      // Check cache first
      if (cache.portfolioSummaries.has(bankId) && !isCacheExpired()) {
        return createApiResponse(cache.portfolioSummaries.get(bankId)!);
      }

      // Generate stable data
      faker.seed(hashString(`portfolio-${bankId}`));

      const summary: PortfolioSummary = {
        total_active_loans: faker.number.int({ min: 50, max: 200 }),
        total_loan_value: faker.number.float({ min: 5000000, max: 50000000, fractionDigits: 2 }),
        average_loan_size: faker.number.float({ min: 25000, max: 75000, fractionDigits: 2 }),
        total_monthly_payments: faker.number.float({ min: 100000, max: 500000, fractionDigits: 2 }),
        default_rate: faker.number.float({ min: 0.01, max: 0.05, fractionDigits: 4 }),
        portfolio_yield: faker.number.float({ min: 0.04, max: 0.08, fractionDigits: 4 }),
        loans_by_status: Object.values(LoanStatus).reduce(
          (acc, status) => ({
            ...acc,
            [status]: faker.number.int({ min: 0, max: 25 }),
          }),
          {} as Record<LoanStatus, number>,
        ),
        loans_by_community: Array.from(
          { length: faker.number.int({ min: 5, max: 15 }) },
          (_, index) => {
            faker.seed(hashString(`${bankId}-community-${index}`));
            return {
              community_id: faker.string.uuid(),
              community_name: faker.company.name() + ' Community',
              loan_count: faker.number.int({ min: 1, max: 8 }),
              total_value: faker.number.float({ min: 50000, max: 500000, fractionDigits: 2 }),
            };
          },
        ),
      };

      // Cache the result
      cache.portfolioSummaries.set(bankId, summary);
      cache.lastGenerated = Date.now();

      return createApiResponse(summary);
    },
  },

  // Financial Metrics
  financial: {
    async getMetrics(bankId: string): Promise<ApiResponse<FinancialMetrics>> {
      await simulateDelay();

      // Check cache first
      const cacheKey = `metrics-${bankId}`;
      if (cache.financialMetrics.has(cacheKey) && !isCacheExpired()) {
        return createApiResponse(cache.financialMetrics.get(cacheKey)!);
      }

      // Generate stable data
      faker.seed(hashString(`metrics-${bankId}`));

      const portfolioSummary = (await mockLendingAPI.bank.getPortfolioSummary(bankId)).data;

      const metrics: FinancialMetrics = {
        portfolio_summary: portfolioSummary,
        monthly_originations: Array.from({ length: 12 }, (_, i) => {
          faker.seed(hashString(`${bankId}-month-${i}`));
          return {
            month: faker.date
              .recent({ days: 30 * (12 - i) })
              .toISOString()
              .slice(0, 7),
            loan_count: faker.number.int({ min: 5, max: 25 }),
            total_value: faker.number.float({ min: 200000, max: 2000000, fractionDigits: 2 }),
          };
        }),
        risk_distribution: ['low', 'medium', 'high', 'very_high'].map((rating, index) => {
          faker.seed(hashString(`${bankId}-risk-${index}`));
          return {
            risk_rating: rating,
            loan_count: faker.number.int({ min: 10, max: 50 }),
            total_value: faker.number.float({ min: 500000, max: 5000000, fractionDigits: 2 }),
          };
        }),
        performance_metrics: {
          loans_30_days_past_due: faker.number.int({ min: 0, max: 5 }),
          loans_60_days_past_due: faker.number.int({ min: 0, max: 3 }),
          loans_90_days_past_due: faker.number.int({ min: 0, max: 2 }),
          charge_offs_ytd: faker.number.float({ min: 0, max: 100000, fractionDigits: 2 }),
          net_interest_margin: faker.number.float({ min: 0.02, max: 0.06, fractionDigits: 4 }),
        },
      };

      // Cache the result
      cache.financialMetrics.set(cacheKey, metrics);
      cache.lastGenerated = Date.now();

      return createApiResponse(metrics);
    },
  },
};

export default mockLendingAPI;
